var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useMemo } from "react";
var FORM_LENGTH = 5;
var isLeagueMatch = function (match) {
    return "homeTeam" in match;
};
var extractTeamsAndScore = function (match) {
    var _a, _b, _c, _d, _e, _f;
    if (isLeagueMatch(match)) {
        var result_1 = match.leagueMatchResult;
        return {
            teamA: match.homeTeam,
            teamB: match.awayTeam,
            scoreA: (_a = result_1 === null || result_1 === void 0 ? void 0 : result_1.homeTeamScore) !== null && _a !== void 0 ? _a : null,
            scoreB: (_b = result_1 === null || result_1 === void 0 ? void 0 : result_1.awayTeamScore) !== null && _b !== void 0 ? _b : null,
            kickOff: match.kickOff,
        };
    }
    if (!((_c = match.teamA) === null || _c === void 0 ? void 0 : _c.id) || !((_d = match.teamB) === null || _d === void 0 ? void 0 : _d.id))
        return null;
    var result = match.intraSquadTournamentMatchResult;
    return {
        teamA: match.teamA,
        teamB: match.teamB,
        scoreA: (_e = result === null || result === void 0 ? void 0 : result.teamAScore) !== null && _e !== void 0 ? _e : null,
        scoreB: (_f = result === null || result === void 0 ? void 0 : result.teamBScore) !== null && _f !== void 0 ? _f : null,
        kickOff: match.kickOff,
    };
};
export var useLeagueTable = function (matches, teams) {
    return useMemo(function () {
        var e_1, _a;
        var table = new Map();
        var teamMatchResults = new Map();
        var ensureTeam = function (team) {
            if (!table.has(team.id)) {
                table.set(team.id, {
                    team: team,
                    played: 0,
                    wins: 0,
                    draws: 0,
                    losses: 0,
                    goalsFor: 0,
                    goalsAgainst: 0,
                    goalDifference: 0,
                    points: 0,
                    form: [],
                });
                teamMatchResults.set(team.id, []);
            }
            return table.get(team.id);
        };
        // Pre-seed teams if provided
        if (teams) {
            teams.forEach(function (team) { return ensureTeam(team); });
        }
        // Initialize all teams and process results in one pass
        matches.forEach(function (match) {
            // Only regular matches count toward league standings
            if (isLeagueMatch(match) && match.matchType !== "regular")
                return;
            var extracted = extractTeamsAndScore(match);
            if (!extracted)
                return;
            var teamA = extracted.teamA, teamB = extracted.teamB, scoreA = extracted.scoreA, scoreB = extracted.scoreB, kickOff = extracted.kickOff;
            ensureTeam(teamA);
            ensureTeam(teamB);
            if (scoreA === null || scoreB === null)
                return;
            var standingA = table.get(teamA.id);
            var standingB = table.get(teamB.id);
            standingA.played += 1;
            standingA.goalsFor += scoreA;
            standingA.goalsAgainst += scoreB;
            standingA.goalDifference = standingA.goalsFor - standingA.goalsAgainst;
            standingB.played += 1;
            standingB.goalsFor += scoreB;
            standingB.goalsAgainst += scoreA;
            standingB.goalDifference = standingB.goalsFor - standingB.goalsAgainst;
            if (scoreA > scoreB) {
                standingA.wins += 1;
                standingA.points += 3;
                standingB.losses += 1;
                teamMatchResults.get(teamA.id).push({ kickOff: kickOff, result: "W" });
                teamMatchResults.get(teamB.id).push({ kickOff: kickOff, result: "L" });
            }
            else if (scoreB > scoreA) {
                standingB.wins += 1;
                standingB.points += 3;
                standingA.losses += 1;
                teamMatchResults.get(teamA.id).push({ kickOff: kickOff, result: "L" });
                teamMatchResults.get(teamB.id).push({ kickOff: kickOff, result: "W" });
            }
            else {
                standingA.draws += 1;
                standingA.points += 1;
                standingB.draws += 1;
                standingB.points += 1;
                teamMatchResults.get(teamA.id).push({ kickOff: kickOff, result: "D" });
                teamMatchResults.get(teamB.id).push({ kickOff: kickOff, result: "D" });
            }
        });
        try {
            // Compute form (last N results, ordered oldest first for display)
            for (var _b = __values(teamMatchResults.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
                var _d = __read(_c.value, 2), teamId = _d[0], results = _d[1];
                var standing = table.get(teamId);
                var sorted = results.sort(function (a, b) { return a.kickOff.localeCompare(b.kickOff); });
                standing.form = sorted.slice(-FORM_LENGTH).map(function (r) { return r.result; });
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return Array.from(table.values()).sort(function (a, b) {
            return b.points - a.points ||
                b.goalDifference - a.goalDifference ||
                b.goalsFor - a.goalsFor ||
                a.team.name.localeCompare(b.team.name);
        });
    }, [matches, teams]);
};
